import ErrorReportService from '@root/core/src/services/error-report-service';
import uniq from '@root/vendor/lodash/uniq';

export const JOB_POSTING_SOURCES = {
  WORKDAY: 'workday',
  LEVER: 'lever',
  PARSNIP_LEVER: 'parsnip_lever',
};
export default class JobPosting {
  static TEAMS = {
    FINANCE: 'Finance',
    ACTUARIAL: 'Actuarial',
    MARKETING: 'Marketing',
    CLAIMS: 'Claims',
    CUSTOMER_SERVICE: 'Customer Service & Sales',
    DATA_SCIENCE: 'Data Science',
    ANALYTICS: 'Analytics',
    ENGINEERING: 'Engineering',
    LEGAL: 'Legal',
    CREATIVE: 'Creative',
    PEOPLE: 'People',
    PRODUCT: 'Product',
    RESEARCH_AND_DEVELOPMENT: 'Research & Development',
    BUSINESS_DEVELOPMENT_AND_STRATEGY: 'Business Dev & Strategy',
  }

  static TEAM_SLUG_MAPPING = {
    [this.TEAMS.FINANCE]: 'finance',
    [this.TEAMS.ACTUARIAL]: 'actuarial',
    [this.TEAMS.MARKETING]: 'marketing',
    [this.TEAMS.CLAIMS]: 'claims',
    [this.TEAMS.CUSTOMER_SERVICE]: 'customer-service-and-sales',
    [this.TEAMS.DATA_SCIENCE]: 'data-science',
    [this.TEAMS.ANALYTICS]: 'analytics',
    [this.TEAMS.ENGINEERING]: 'engineering',
    [this.TEAMS.LEGAL]: 'legal',
    [this.TEAMS.CREATIVE]: 'creative',
    [this.TEAMS.PEOPLE]: 'people',
    [this.TEAMS.PRODUCT]: 'product',
    [this.TEAMS.RESEARCH_AND_DEVELOPMENT]: 'research-and-development',
    [this.TEAMS.BUSINESS_DEVELOPMENT_AND_STRATEGY]: 'business-development-and-strategy',
  };

  static getLocationsFromJobPostings(jobPostings) {
    return uniq(jobPostings.map(({ location }) => location));
  }

  static getColorByTeam({ team }) {
    const colorMap = {
      '#7c5e93': [
        this.TEAMS.ACTUARIAL,
        this.TEAMS.CLAIMS,
        this.TEAMS.CUSTOMER_SERVICE,
        this.TEAMS.ANALYTICS,
      ],
      '#5ac8fa': [
        this.TEAMS.DATA_SCIENCE,
        this.TEAMS.CREATIVE,
        this.TEAMS.ENGINEERING,
        this.TEAMS.RESEARCH_AND_DEVELOPMENT,
      ],
      '#ff2d55': [
        this.TEAMS.FINANCE,
        this.TEAMS.LEGAL,
        this.TEAMS.PEOPLE,
      ],
      '#72cf61': [
        this.TEAMS.MARKETING,
        this.TEAMS.PRODUCT,
        this.TEAMS.BUSINESS_DEVELOPMENT_AND_STRATEGY,
      ],
    };

    return Object.keys(colorMap).filter((k) => colorMap[k].includes(team.toLowerCase()))[0] || '#7c5e93';
  }

  static createLeverJobPosting(jobPosting) {
    const description = transformMarkup(jobPosting.description) + jobPosting.lists?.map(({ text, content }) => `<h4>${text}</h4><ul>${content}</ul>`).join('');
    const jobTitle = jobPosting.text;
    const id = jobPosting.lever_id;
    const location = jobPosting.categories.location;
    const team = LeverTeamMapping[jobPosting.categories.team] || jobPosting.categories.team;
    const slug = this.TEAM_SLUG_MAPPING[team];

    return new JobPosting({
      description,
      jobTitle,
      id,
      location,
      team,
      slug,
    }, JOB_POSTING_SOURCES.LEVER);
  }

  static createWorkdayJobPosting(jobPosting) {
    const description = jobPosting.jobDescription;
    const jobTitle = jobPosting.jobPosting;
    const id = jobPosting.id;
    const location = jobPosting.primaryLocation;
    const team = jobPosting.team;
    const applyURL = jobPosting.applyURL;

    return new JobPosting({
      description,
      jobTitle,
      id,
      location,
      team,
      applyURL,
    }, JOB_POSTING_SOURCES.WORKDAY);
  }

  constructor(jobPosting, type) {
    this.description = jobPosting.description;
    this.jobTitle = jobPosting.jobTitle;
    this.id = jobPosting.id;
    this.location = jobPosting.location;
    this.team = jobPosting.team;
    this.applyURL = jobPosting.applyURL;
    this.slug = jobPosting.slug;

    if (!this.team) {
      ErrorReportService.reportError({
        error: new Error(`${type} provided a team that we have not defined: ${jobPosting.team}`),
        caughtAt: `JobPosting constructor: ${JSON.stringify(jobPosting)}`,
      });
    }
  }
}

const LeverTeamMapping = {
  'Finance': JobPosting.TEAMS.FINANCE,
  'Actuary': JobPosting.TEAMS.ACTUARIAL,
  'Marketing': JobPosting.TEAMS.MARKETING,
  'Claims': JobPosting.TEAMS.CLAIMS,
  'Customer Service': JobPosting.TEAMS.CUSTOMER_SERVICE,
  'Data Science': JobPosting.TEAMS.DATA_SCIENCE,
  'Analytics': JobPosting.TEAMS.ANALYTICS,
  'Engineering': JobPosting.TEAMS.ENGINEERING,
  'Legal': JobPosting.TEAMS.LEGAL,
  'Design': JobPosting.TEAMS.CREATIVE,
  'People': JobPosting.TEAMS.PEOPLE,
  'Product': JobPosting.TEAMS.PRODUCT,
  'Research and Development': JobPosting.TEAMS.RESEARCH_AND_DEVELOPMENT,
  'Business Development and Strategy': JobPosting.TEAMS.BUSINESS_DEVELOPMENT_AND_STRATEGY,
  'HR': JobPosting.TEAMS.PEOPLE,
};

// Remove all instances of inline styles for font-size unless it's being set to 18px. Lever uses 18px for headlines rather than using a headline tag.
const transformMarkup = (descriptionMarkup) => {
  if (!descriptionMarkup) { return null; }

  return descriptionMarkup.replace(/style="font-size: (?!18px)[^"]*"/g, '');
};
